import * as React from 'react';

import { DocumentType } from '@rematter/frontend-lib-api-client';
import { useGenFileDownloadUrl } from '@rematter/modules/customer-portal/_graphql/mutationFileDownloadUrl';
import { ensureValue } from '@rematter/ui-web';

import { createMutationHook } from '~/shared/providers/api';

export type DocumentInput =
  | {
      documentType: DocumentType;
      loadId: string;
      bookingId?: never;
      salesOrderId?: never;
      invoiceNumber?: never;
    }
  | {
      documentType: 'Invoice';
      invoiceNumber: string;
      bookingId?: never;
      loadId?: never;
      salesOrderId?: never;
    }
  | {
      documentType: 'SalesOrder' | 'ShippingSummary';
      salesOrderId: string;
      loadId?: never;
      bookingId?: never;
      invoiceNumber?: never;
    }
  | {
      documentType: 'BookingInvoice';
      bookingId: string;
      loadId?: never;
      salesOrderId?: never;
      invoiceNumber?: never;
    }
  | {
      documentType: 'LoadInvoice';
      loadId: string;
      invoiceNumber: string;
      bookingId?: never;
      salesOrderId?: never;
    };

const useGenerateDocumentsMutation = createMutationHook(
  'customerPortal.generateDocuments',
  ({ documents }: { documents: DocumentInput[] }) => ({
    tradeGenerateDocument: {
      __args: {
        input: {
          documents: documents,
        },
      },
      documents: {
        fileId: true,
        __scalar: true,
      },
    },
  }),
  {
    select: ({ tradeGenerateDocument }) =>
      tradeGenerateDocument.documents.map((document) => document),
  },
);

export const useGenerateDocuments = () => {
  const {
    mutateAsync: generateFileDownloadUrl,
    isPending: isGeneratingFileDownloadUrl,
  } = useGenFileDownloadUrl();
  const { mutateAsync: generateDocuments, isPending: isGeneratingDocuments } =
    useGenerateDocumentsMutation();

  const call = React.useCallback(
    async (documents: DocumentInput[]) => {
      const fileIds = await generateDocuments({ documents });
      const fileUrls = await generateFileDownloadUrl({
        fileIds: fileIds.map((file) => ensureValue(file.fileId)),
      });

      return fileUrls.map((file, index) => ({
        ...file,
        documentType: documents[index].documentType,
      }));
    },
    [generateDocuments, generateFileDownloadUrl],
  );

  const isPending = isGeneratingFileDownloadUrl || isGeneratingDocuments;

  return [call, { isPending }] as const;
};
